/** @jsxImportSource @emotion/react */

import React, { useEffect } from "react";
import interact from "interactjs";
import Helper from "../functions/Helper.js";
import { ArrowMarker, ScreenMarker, ShotMarker } from "../markers";
import {
  tap,
  doubleTap,
  moveObject,
  mouseEnter,
  mouseLeave,
  interactionStart,
} from "../reducers/slices/phases.js";
import { useTheme } from "@mui/material/styles";
import useCurrentValue from "../../hooks/useCurrentValue.js";

const getStrokeColor = (status, defaultColor, theme) => {
  if (status === "SELECTED_MULTIPLE" || status === "HOVERING_ANIMATOR") {
    return theme.palette.info.main;
  } else {
    return defaultColor;
  }
};

const getStrokeDashArray = (props) => {
  switch (props.subType) {
    case "pass":
      return "1.2  0.4";
    case "shot":
      return "1.2  0.4";

    default:
      return "";
  }
};

const getMarkerEnd = (props) => {
  switch (props.subType) {
    case "screen":
      return "url(#screen_" + props.uId + ")";
    case "shot":
      return "url(#shot_" + props.uId + ")";
    default:
      return "url(#arrow_" + props.uId + ")";
  }
};

export default function Arrow(props) {
  const theme = useTheme();

  let arrowLength =
    Helper.distance(props.s, props.m) + Helper.distance(props.m, props.e);
  let pixelPerFoot = props.pixelPerFoot === 0 ? 100000 : props.pixelPerFoot;

  const dispatch = props.dispatch;

  let objectSizeFactor = props.objectSizeFactor ?? 1;
  let { arrowStrokeWidth, playerCircleRadius } = props.objectSizes;

  arrowStrokeWidth *= objectSizeFactor;
  playerCircleRadius *= objectSizeFactor;

  const playerCircleRadiusRef = useCurrentValue(playerCircleRadius);

  const propsRef = useCurrentValue(props);

  let {
    minX: viewBoxX,
    minY: viewBoxY,
    viewBoxWidth,
    viewBoxHeight,
  } = Helper.getMaxCoordinates(props.court);

  useEffect(() => {
    if (props.editable) {
      interact("#" + props.uId + "_si")
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("tap", (event) => {
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("doubletap", (event) => {
          dispatch(
            doubleTap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            if (
              propsRef.current.links.filter((link) => link.point === "e")
                .length > 0
            ) {
              document
                ?.getElementById(props.uId + "_e")
                ?.classList?.add("hide_circle");
            }
            dispatch(interactionStart({ id: props.id, point: "s" }));
          },
          onend: (event) => {
            updateStore(event, "s");
            Array.from(document.getElementsByClassName("hide_circle")).forEach(
              (el) => el.classList.remove("hide_circle")
            );
          },
          onmove: (event) => dragMoveListener(event, "s"),
        });

      interact("#" + props.uId + "_ei")
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("tap", (event) => {
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("doubletap", (event) => {
          dispatch(
            doubleTap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            if (
              propsRef.current.links.filter((link) => link.point === "s")
                .length > 0 &&
              propsRef.current.shape < 2
            ) {
              document
                ?.getElementById(props.uId + "_s")
                ?.classList?.add("hide_circle");
            }
            dispatch(interactionStart({ id: props.id, point: "e" }));
          },
          onend: (event) => {
            updateStore(event, "e");
            Array.from(document.getElementsByClassName("hide_circle")).forEach(
              (el) => el.classList.remove("hide_circle")
            );
          },
          onmove: (event) => dragMoveListener(event, "e"),
        });

      interact("#" + props.uId + "_o")
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("tap", (event) => {
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("doubletap", (event) => {
          dispatch(
            doubleTap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrictRect({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            dispatch(interactionStart({ id: props.id, point: "p" }));
          },
          onend: (event) => updateStore(event, "p"),
          onmove: (event) => dragMoveListener(event, "p"),
        });

      interact("#" + props.uId + "_mi")
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("tap", (event) => {
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("doubletap", (event) => {
          dispatch(doubleTap({ id: props.id }));
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            if (
              propsRef.current.links.filter((link) => link.point === "e")
                .length > 0
            ) {
              document
                ?.getElementById(props.uId + "_e")
                ?.classList?.add("hide_circle");
            }
            if (
              propsRef.current.links.filter((link) => link.point === "s")
                .length > 0
            ) {
              document
                ?.getElementById(props.uId + "_s")
                ?.classList?.add("hide_circle");
            }

            dispatch(interactionStart({ id: props.id }));
          },
          onend: (event) => {
            updateStore(event, "m");
            Array.from(document.getElementsByClassName("hide_circle")).forEach(
              (el) => el.classList.remove("hide_circle")
            );
          },
          onmove: (event) => dragMoveListener(event, "m"),
        });

      interact("#" + props.uId + "_h1i")
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("tap", (event) => {
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("doubletap", (event) => {
          dispatch(
            doubleTap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            if (
              propsRef.current.links.filter((link) => link.point === "s")
                .length > 0
            ) {
              document
                ?.getElementById(props.uId + "_s")
                ?.classList?.add("hide_circle");
            }
            dispatch(interactionStart({ id: props.id }));
          },
          onend: (event) => {
            Array.from(document.getElementsByClassName("hide_circle")).forEach(
              (el) => el.classList.remove("hide_circle")
            );
            updateStore(event, "h1");
          },
          onmove: (event) => dragMoveListener(event, "h1"),
        });

      interact("#" + props.uId + "_h2i")
        .styleCursor(false)
        .on("contextmenu", (event) => {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("tap", (event) => {
          dispatch(
            tap({
              id: props.id,
              ctrlKey:
                event.originalEvent.ctrlKey || event.originalEvent.metaKey,
            })
          );
        })
        .on("doubletap", (event) => {
          dispatch(doubleTap({ id: props.id }));
        })
        .draggable({
          modifiers: [
            interact.modifiers.restrict({
              restriction: "#editor_wrapper",
            }),
          ],
          onstart: () => {
            if (
              propsRef.current.links.filter((link) => link.point === "e")
                .length > 0
            ) {
              document
                ?.getElementById(props.uId + "_e")
                ?.classList?.add("hide_circle");
            }
            dispatch(interactionStart({ id: props.id }));
          },
          onend: (event) => {
            Array.from(document.getElementsByClassName("hide_circle")).forEach(
              (el) => el.classList.remove("hide_circle")
            );
            updateStore(event, "h2");
          },
          onmove: (event) => dragMoveListener(event, "h2"),
        });

      return () => {
        interact("#" + props.uId + "_si").unset();
        interact("#" + props.uId + "_ei").unset();
        interact("#" + props.uId + "_o").unset();
        interact("#" + props.uId + "_mi").unset();
        interact("#" + props.uId + "_h1i").unset();
        interact("#" + props.uId + "_h2i").unset();
      };
    }
  }, []); //eslint-disable-line

  function dragMoveListener(event, pointType) {
    let target = event.target;

    let handlePoints = ["s", "m", "e", "h1", "h2"];
    let domPoints = {
      s: {
        visible: document.getElementById(props.uId + "_s"),
        invisible: document.getElementById(props.uId + "_si"),
      },
      m: {
        visible: document.getElementById(props.uId + "_m"),
        invisible: document.getElementById(props.uId + "_mi"),
      },
      e: {
        visible: document.getElementById(props.uId + "_e"),
        invisible: document.getElementById(props.uId + "_ei"),
      },
      h1: {
        visible: document.getElementById(props.uId + "_h1"),
        invisible: document.getElementById(props.uId + "_h1i"),
      },
      h2: {
        visible: document.getElementById(props.uId + "_h2"),
        invisible: document.getElementById(props.uId + "_h2i"),
      },
    };

    let deltaX =
      (parseFloat(target.getAttribute("data-x")) || 0) +
      event.dx / propsRef.current.pixelPerFoot;
    let deltaY =
      (parseFloat(target.getAttribute("data-y")) || 0) +
      event.dy / propsRef.current.pixelPerFoot;
    let shape = propsRef.current.shape;
    target.setAttribute("data-x", deltaX);
    target.setAttribute("data-y", deltaY);

    let svg = document.querySelector("#editor_court");
    let pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    pt = pt.matrixTransform(svg.getScreenCTM().inverse());

    if (pointType === "p") {
      for (let handlePoint of handlePoints) {
        if (domPoints[handlePoint].visible) {
          domPoints[handlePoint].visible.style.transform =
            "translate(" + deltaX + "px, " + deltaY + "px)";
          domPoints[handlePoint].invisible.style.transform =
            "translate(" + deltaX + "px, " + deltaY + "px)";
          domPoints[handlePoint].visible.setAttribute("data-x", deltaX);
          domPoints[handlePoint].visible.setAttribute("data-y", deltaY);
        }
      }

      let newStart = [
        propsRef.current.s[0] + deltaX,
        propsRef.current.s[1] + deltaY,
      ];
      let newEnd = [
        propsRef.current.e[0] + deltaX,
        propsRef.current.e[1] + deltaY,
      ];
      let newMiddle = [
        propsRef.current.m[0] + deltaX,
        propsRef.current.m[1] + deltaY,
      ];
      let newH1 = propsRef.current.h1
        ? [propsRef.current.h1[0] + deltaX, propsRef.current.h1[1] + deltaY]
        : undefined;
      let newH2 = propsRef.current.h2
        ? [propsRef.current.h2[0] + deltaX, propsRef.current.h2[1] + deltaY]
        : undefined;

      let newAction = {
        ...propsRef.current,
        s: newStart,
        m: newMiddle,
        e: newEnd,
        h1: newH1,
        h2: newH2,
      };

      document.getElementById(props.uId + "_p").setAttribute(
        "d",
        Helper.createPathV2(
          newAction,
          propsRef.current.links.filter(
            (link) =>
              link.player.subType !== "handoff" &&
              link.object.subType !== "handoff"
          ),
          playerCircleRadiusRef.current
        )
      );
      propsRef.current.highlightPossibleLinks(newAction);
    } else {
      let currentArrowPos = Helper.onlyPoints(propsRef.current);
      let arrowChanges = Helper.transformArrow(
        currentArrowPos,
        { point: pointType, pos: [pt.x, pt.y] },
        shape
      );
      let nextArrowPos = { ...currentArrowPos, ...arrowChanges };

      for (const [point, pos] of Object.entries(arrowChanges)) {
        let delta = [
          pos[0] - currentArrowPos[point][0],
          pos[1] - currentArrowPos[point][1],
        ];

        if (domPoints[point].visible) {
          domPoints[point].visible.style.transform =
            "translate(" + delta[0] + "px, " + delta[1] + "px)";
          domPoints[point].invisible.style.transform =
            "translate(" + delta[0] + "px, " + delta[1] + "px)";
        }
      }

      let newAction = {
        ...propsRef.current,
        shape: shape,
        s: nextArrowPos.s,
        m: nextArrowPos.m,
        e: nextArrowPos.e,
        h1: nextArrowPos.h1,
        h2: nextArrowPos.h2,
      };

      document
        .getElementById(props.uId + "_p")
        .setAttribute(
          "d",
          Helper.createPathV2(
            newAction,
            propsRef.current.links,
            playerCircleRadiusRef.current
          )
        );

      propsRef.current.highlightPossibleLinks(newAction);
    }
  }

  function updateStore(event, pointType) {
    Array.from(document.getElementsByClassName("target_hover")).forEach(
      (el) => {
        el.classList.remove("target_hover");
      }
    );

    let target = event.target;
    let newData;
    let svg = document.querySelector("#editor_court");
    let pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    pt = pt.matrixTransform(svg.getScreenCTM().inverse());
    let shape = propsRef.current.shape;
    let deltaX = parseFloat(target.getAttribute("data-x"));
    let deltaY = parseFloat(target.getAttribute("data-y"));

    if (pointType === "p") {
      newData = {
        s: [propsRef.current.s[0] + deltaX, propsRef.current.s[1] + deltaY],
        m: [propsRef.current.m[0] + deltaX, propsRef.current.m[1] + deltaY],
        e: [propsRef.current.e[0] + deltaX, propsRef.current.e[1] + deltaY],
        h1: propsRef.current.h1
          ? [propsRef.current.h1[0] + deltaX, propsRef.current.h1[1] + deltaY]
          : undefined,
        h2: propsRef.current.h2
          ? [propsRef.current.h2[0] + deltaX, propsRef.current.h2[1] + deltaY]
          : undefined,
      };
    } else {
      let currentArrowPos = {
        s: propsRef.current.s,
        m: propsRef.current.m,
        e: propsRef.current.e,
        h1: propsRef.current.h1,
        h2: propsRef.current.h2,
      };

      let arrowChanges = Helper.transformArrow(
        currentArrowPos,
        { point: pointType, pos: [pt.x, pt.y] },
        shape
      );
      newData = { ...currentArrowPos, ...arrowChanges };
    }
    dispatch(
      moveObject({
        data: { ...newData },
        id: props.id,
        playerCircleRadius: playerCircleRadius,
      })
    );

    let ids = [
      props.uId + "_s",
      props.uId + "_e",
      props.uId + "_p",
      props.uId + "_m",
      props.uId + "_o",
      props.uId + "_si",
      props.uId + "_mi",
      props.uId + "_ei",
      props.uId + "_h1i",
      props.uId + "_h2i",
      props.uId + "_h1",
      props.uId + "_h2",
    ];
    ids.forEach((item, i) => {
      let target = document.getElementById(item);
      if (target) {
        target.style.transform = "";
        target.setAttribute("data-x", 0);
        target.setAttribute("data-y", 0);
      }
    });
  }

  let linkedEnds = props.links.map((x) => x.point);

  let drawPath =
    props.subType === "dribble"
      ? Helper.createWavyPath(
          Helper.calculatePointsOnCurve(
            linkedEnds,
            props.shape,
            props.s,
            props.m,
            props.e,
            props.h1,
            props.h2,
            playerCircleRadius
          ),
          pixelPerFoot
        )
      : Helper.createPathV2(
          props,
          props.links.filter(
            (link) =>
              link.player.subType !== "handoff" &&
              link.object.subType !== "handoff"
          ),
          playerCircleRadius
        );

  let adjustedEndPoints = Helper.adjustPointsV2(
    props,
    props.links.filter(
      (link) =>
        link.player.subType !== "handoff" && link.object.subType !== "handoff"
    ),
    playerCircleRadius
  );

  const getArrowMarker = (subType) => {
    switch (subType) {
      case "screen":
        return (
          <ScreenMarker
            id={"mask_" + props.uId + "_arrow_marker"}
            width={13}
            length={2}
            color="white"
          />
        );
      case "shot":
        return (
          <ShotMarker
            id={"mask_" + props.uId + "_arrow_marker"}
            width={13}
            color={"white"}
          />
        );
      default:
        return (
          <ArrowMarker
            id={"mask_" + props.uId + "_arrow_marker"}
            width={8}
            length={7}
            color="white"
          />
        );
    }
  };

  const effectiveStatus = Helper.getEffectiveStatus(props);
  const strokeColor = getStrokeColor(effectiveStatus, props.color, theme);

  const showDragCircles = ["SELECTED", "HOVERING", "INTERACTING"].includes(
    effectiveStatus
  );

  useEffect(() => {
    if (props.editable) {
      if (showDragCircles) {
        props.allLinkedPlayers.forEach((player) => {
          document
            .getElementById("editor_" + player.id)
            ?.classList?.add("linked_hovering");
        });
      } else {
        props.allLinkedPlayers.forEach((player) => {
          document
            .getElementById("editor_" + player.id)
            ?.classList?.remove("linked_hovering");
        });
      }
      return () => {
        props.allLinkedPlayers.forEach((player) => {
          document
            .getElementById("editor_" + player.id)
            ?.classList?.remove("linked_hovering");
        });
      };
    }
  }, [showDragCircles]);

  return (
    <>
      <defs>
        <ArrowMarker
          id={"arrow_" + props.uId}
          width={8}
          length={7}
          color={strokeColor}
        />
        <ScreenMarker
          id={"screen_" + props.uId}
          width={13}
          length={2}
          color={strokeColor}
        />
        <ShotMarker id={"shot_" + props.uId} width={13} color={strokeColor} />

        {props.animatable && (
          <>
            {getArrowMarker(props.subType)}
            <mask id={"mask_" + props.uId}>
              <path
                markerEnd={"url(#mask_" + props.uId + "_arrow_marker"}
                d={drawPath}
                strokeWidth={arrowStrokeWidth}
                fill="none"
                stroke={"white"}
              />
            </mask>
          </>
        )}
      </defs>

      <g
        mask={props.animatable ? "url(#mask_" + props.uId + ")" : ""}
        id={props.uId}
        className={
          "draggable " + (props.linkedObjectHovering ? "linked_hovering" : "")
        }
        onMouseEnter={() => {
          if (
            props.editable &&
            props.status === "NOT_SELECTED" &&
            !props.interaction
          ) {
            dispatch(mouseEnter({ id: props.id }));
          }
        }}
        onMouseLeave={() => {
          if (
            props.editable &&
            props.status === "HOVERING" &&
            !props.interaction
          ) {
            dispatch(mouseLeave({ id: props.id }));
          }
        }}
      >
        {props.editable &&
          ["cut", "screen", "dribble"].includes(props.subType) && (
            <circle
              className={props.linkedObjectHovering ? "linked_hovering" : ""}
              css={{
                fill: "none",

                ".linked_hovering &": {
                  fill: "rgb(240, 241, 243)",
                  fillOpacity: 0.6,
                },
                ".target_hover &": {
                  fill: "rgb(240, 241, 243)",
                  fillOpacity: 1,
                },
              }}
              id={props.uId + "_hover_circle"}
              cx={props.e[0]}
              cy={props.e[1]}
              r={playerCircleRadius}
            />
          )}

        {props.editable && ["pass"].includes(props.subType) && (
          <circle
            css={{
              fill: "none",

              ".target_hover &": {
                fill: "rgb(240, 241, 243)",
                fillOpacity: 1,
              },
            }}
            id={props.uId + "_hover_circle"}
            cx={props.e[0]}
            cy={props.e[1]}
            r={playerCircleRadius}
          />
        )}

        <path
          key={props.id + "_" + props.status}
          className="draggable"
          id={props.uId + "_p"}
          d={drawPath}
          markerEnd={getMarkerEnd(props)}
          strokeDasharray={getStrokeDashArray(props)}
          strokeWidth={arrowStrokeWidth}
          fill="none"
          stroke={strokeColor}
        />

        {props.animatable && (
          <rect
            id={props.uId + "_rect_animatable"}
            x={viewBoxX}
            y={viewBoxY}
            width={viewBoxWidth}
            height={viewBoxHeight}
            fill="none"
            stroke="none"
            strokeWidth={0}
          />
        )}

        <path
          className="draggable"
          style={{ cursor: props.editable ? "move" : "inherit" }}
          id={props.uId + "_o"}
          d={drawPath}
          strokeWidth={40 / pixelPerFoot}
          stroke={"transparent"}
          fill="none"
        />
        {showDragCircles && (
          <>
            <circle
              className="draggable"
              id={props.uId + "_s"}
              style={{ cursor: "grabbing" }}
              fill="rgb(10,132,255)"
              cx={adjustedEndPoints.s[0]}
              cy={adjustedEndPoints.s[1]}
              r={6 / pixelPerFoot}
              strokeWidth={2 / pixelPerFoot}
              stroke="white"
              shapeRendering="geometricPrecision"
            />
            <circle
              className="draggable"
              id={props.uId + "_si"}
              fill="transparent"
              style={{ cursor: "grabbing" }}
              cx={adjustedEndPoints.s[0]}
              cy={adjustedEndPoints.s[1]}
              r={(arrowLength > 10 ? 20 : 10) / pixelPerFoot}
              strokeWidth={3 / pixelPerFoot}
              shapeRendering="geometricPrecision"
            />
            <circle
              className="draggable"
              id={props.uId + "_e"}
              style={{ cursor: "grabbing" }}
              fill="rgb(10,132,255)"
              cx={adjustedEndPoints.e[0]}
              cy={adjustedEndPoints.e[1]}
              r={6 / pixelPerFoot}
              strokeWidth={2 / pixelPerFoot}
              stroke="white"
              shapeRendering="geometricPrecision"
            />
            <circle
              className="draggable"
              id={props.uId + "_ei"}
              style={{ cursor: "grabbing" }}
              fill="transparent"
              cx={adjustedEndPoints.e[0]}
              cy={adjustedEndPoints.e[1]}
              r={(arrowLength > 10 ? 20 : 10) / pixelPerFoot}
              strokeWidth={3 / pixelPerFoot}
              shapeRendering="geometricPrecision"
            />

            {props.shape !== 0 && (
              <>
                <circle
                  className="draggable"
                  id={props.uId + "_m"}
                  style={{ cursor: "grabbing" }}
                  fill="rgb(10,132,255)"
                  cx={props.m[0]}
                  cy={props.m[1]}
                  r={6 / pixelPerFoot}
                  strokeWidth={2 / pixelPerFoot}
                  stroke="white"
                  shapeRendering="geometricPrecision"
                />
                <circle
                  className="draggable"
                  id={props.uId + "_mi"}
                  style={{ cursor: "grabbing" }}
                  fill="transparent"
                  cx={props.m[0]}
                  cy={props.m[1]}
                  r={(arrowLength > 10 ? 20 : 10) / pixelPerFoot}
                  strokeWidth={3 / pixelPerFoot}
                  shapeRendering="geometricPrecision"
                />
              </>
            )}

            {/*h1 helper point for shape 2 and 3*/}
            {props.h1 !== undefined && (
              <>
                <circle
                  className="draggable"
                  id={props.uId + "_h1"}
                  style={{ cursor: "grabbing" }}
                  fill="#32CD32"
                  cx={props.h1[0]}
                  cy={props.h1[1]}
                  r={5 / pixelPerFoot}
                  strokeWidth={2 / pixelPerFoot}
                  stroke="white"
                  shapeRendering="geometricPrecision"
                />

                <circle
                  className="draggable"
                  id={props.uId + "_h1i"}
                  style={{ cursor: "grabbing" }}
                  fill="transparent"
                  cx={props.h1[0]}
                  cy={props.h1[1]}
                  r={15 / pixelPerFoot}
                  strokeWidth={3 / pixelPerFoot}
                  shapeRendering="geometricPrecision"
                />
              </>
            )}

            {props.h2 !== undefined && (
              <>
                <circle
                  className="draggable"
                  id={props.uId + "_h2"}
                  style={{ cursor: "grabbing" }}
                  fill="#32CD32"
                  cx={props.h2[0]}
                  cy={props.h2[1]}
                  r={5 / pixelPerFoot}
                  strokeWidth={2 / pixelPerFoot}
                  stroke="white"
                  shapeRendering="geometricPrecision"
                />

                <circle
                  className="draggable"
                  id={props.uId + "_h2i"}
                  style={{ cursor: "grabbing" }}
                  fill="transparent"
                  cx={props.h2[0]}
                  cy={props.h2[1]}
                  r={15 / pixelPerFoot}
                  strokeWidth={3 / pixelPerFoot}
                  shapeRendering="geometricPrecision"
                />
              </>
            )}
          </>
        )}
      </g>
    </>
  );
}
